import type { AnyID } from '@types'
import { isRelaxIntegratedUnitByDefaults } from '@utils/atemporal/relaxArmVariants'
import { resolveLayoutWidth } from '@utils/linkedArms/armPlacement'
import { Quaternion, Vector3 } from 'three'
import { createArmRowContext, type ArmPlacementSlot, type RowSide } from './armRowContext'
import { allowedArmSideForUnit } from './singleArmSeriesRules'

const getWorldPos = (item: any) => {
  if (item?.matrix) {
    const pos = new Vector3()
    item.matrix.decompose(pos, new Quaternion(), new Vector3())
    return pos
  }
  return new Vector3(Number(item?.x || 0), Number(item?.y || 0), Number(item?.z || 0))
}

const getUnitXAxis = (unit: any) => {
  const q = new Quaternion()
  unit.matrix.decompose(new Vector3(), q, new Vector3())
  return new Vector3(1, 0, 0).applyQuaternion(q)
}

const lipAllowsHandedness = (lip: ArmPlacementSlot) => {
  if (isRelaxIntegratedUnitByDefaults(lip.unit?.catalogID)) return false
  const handedSide = allowedArmSideForUnit(lip.unit?.catalogID)
  return handedSide == null || lip.side === handedSide
}

const getLipApproachPoint = (unit: any, side: RowSide) => {
  const unitPos = getWorldPos(unit)
  const unitX = getUnitXAxis(unit)
  const unitWidth = resolveLayoutWidth(unit) || Number(unit?.width || 0)
  if (side === 1) {
    return unitPos.clone().add(unitX.clone().multiplyScalar(unitWidth))
  }
  return unitPos.clone()
}

const countArmsOnLip = (
  ctx: ReturnType<typeof createArmRowContext>,
  lip: ArmPlacementSlot,
  getArmSideSign: (unit: any, arm: any) => RowSide,
) =>
  ctx
    .getArmsOnUnit(String(lip.unit.id))
    .filter((arm) => getArmSideSign(lip.unit, arm) === lip.side).length

/**
 * Elige un labio exterior libre para colocar un brazo nuevo.
 * Soporta filas rectas y grupos en L/C/U (rinconero, corner XL oreja).
 */
export function pickExteriorArmPlacement(
  items: Map<AnyID, any>,
  referenceUnit: any,
  options: {
    excludeArmId?: string
    getArmSideSign: (unit: any, arm: any) => RowSide
    preferredSideSign?: RowSide
  },
): ArmPlacementSlot | null {
  const ctx = createArmRowContext(items, referenceUnit, options)
  const lips = ctx.listExteriorLips().filter(lipAllowsHandedness)
  if (!lips.length) return null

  const freeLips = lips.filter((lip) => !ctx.hasArmOnSide(lip.unit, lip.side))
  if (freeLips.length) {
    const preferred = options.preferredSideSign
    if (preferred === 1 || preferred === -1) {
      const preferredMatch = freeLips.find((lip) => lip.side === preferred)
      if (preferredMatch) return preferredMatch
    }
    const refMatch = freeLips.find((lip) => String(lip.unit.id) === String(referenceUnit.id))
    if (refMatch) return refMatch
    return freeLips[0]
  }

  const weighted = lips.map((lip) => ({
    lip,
    armCount: countArmsOnLip(ctx, lip, options.getArmSideSign),
  }))
  weighted.sort((a, b) => a.armCount - b.armCount)
  return weighted[0]?.lip ?? null
}

/**
 * Reancla un brazo existente al labio exterior más adecuado del grupo.
 *
 * Tras dibujar una unidad nueva (`referenceUnit`), prioridad:
 * 1) si el padre sigue teniendo ese labio exterior → el brazo NO se mueve;
 * 2) labio exterior de la unidad nueva en el mismo lado (migrar al nuevo extremo);
 * 3) labio exterior libre más cercano (nunca uno ya ocupado por otro brazo).
 */
export function resolveExteriorLipForArm(
  items: Map<AnyID, any>,
  arm: any,
  referenceUnit: any,
  getArmSideSign: (unit: any, arm: any) => RowSide,
  options?: { claimedLipKeys?: ReadonlySet<string> },
): ArmPlacementSlot | null {
  const ctx = createArmRowContext(items, referenceUnit, {
    excludeArmId: String(arm.id),
    getArmSideSign,
  })
  const lips = ctx.listExteriorLips().filter(lipAllowsHandedness)
  if (!lips.length) return null

  const claimed = options?.claimedLipKeys
  const isClaimed = (lip: ArmPlacementSlot) =>
    Boolean(claimed?.has(`${String(lip.unit.id)}:${lip.side}`))
  const isFreeLip = (lip: ArmPlacementSlot) =>
    !isClaimed(lip) && !ctx.hasArmOnSide(lip.unit, lip.side)

  const parent = arm.firstParentID ? items.get(arm.firstParentID as AnyID) : null
  const previousSide: RowSide | null =
    parent?.matrix && arm?.matrix ? getArmSideSign(parent, arm) : null
  const referenceId = String(referenceUnit?.id ?? '')

  // Si el padre sigue siendo extremo en ese lado, no tocar el brazo.
  if (parent?.matrix && previousSide != null) {
    const parentLip = lips.find(
      (lip) => String(lip.unit.id) === String(parent.id) && lip.side === previousSide,
    )
    if (parentLip && !isClaimed(parentLip)) return parentLip
  }

  // Solo migrar a la unidad nueva si el padre ya no es exterior en ese lado.
  if (referenceId && previousSide != null) {
    const newUnitLip = lips.find(
      (lip) => String(lip.unit.id) === referenceId && lip.side === previousSide,
    )
    if (newUnitLip && isFreeLip(newUnitLip)) return newUnitLip
  }

  if (parent?.matrix) {
    const anyParentLip = lips.find(
      (lip) => String(lip.unit.id) === String(parent.id) && isFreeLip(lip),
    )
    if (anyParentLip) return anyParentLip
  }

  const armPos = getWorldPos(arm)
  let best: ArmPlacementSlot | null = null
  let bestDist = Number.POSITIVE_INFINITY

  for (const lip of lips) {
    if (!isFreeLip(lip)) continue
    const approachPoint = getLipApproachPoint(lip.unit, lip.side)
    const dist = armPos.distanceTo(approachPoint)
    if (dist < bestDist) {
      bestDist = dist
      best = lip
    }
  }

  return best
}
